import React, { useState, useEffect } from 'react';
import './DocumentsList.css';
import { ReactComponent as DownloadIcon } from '../../assets/icons/export.svg';
import Button from '../Button/Button';
import { formatDate } from '../../utils/entityUtils';
import { DOCUMENT_TYPE_LABELS } from '../../services/documentService';

interface DocumentType {
  id: number;
  type: string;
  displayLabel?: string; // Label d'affichage personnalisé (ex: "Contrat n°25-001")
  commentaire?: string;
  updated_at: string;
  key?: string; // Clé UUID pour les documents entreprise et accord-cadre
  category?: 'entreprise' | 'accord_cadre' | 'commun'; // Catégorie du document
  filename?: string;
  binaire?: string; // Contenu base64 pour les contrats AC
}

interface DocumentsListProps {
  documents: DocumentType[];
  variant?: 'fullWidth' | 'inline' | 'grid';
  lineheight?: number;
  showIcon?: boolean; // conservé pour compatibilité (ignoré dans le nouveau design)
  size?: 'default' | 'smaller';
  downloadUrlPrefix?: string;
  /** Callback pour afficher la carte "+" à la fin de la liste */
  onAdd?: () => void;
  /** Libellé affiché sous le "+" (défaut : "Ajouter") */
  onAddLabel?: string;
  /** Désactiver la carte "+" */
  onAddDisabled?: boolean;
  /** Passe la carte "+" en rouge quand un document obligatoire manque (calque sur l'etat erreur des References) */
  addCardError?: boolean;
  /** Callback appelé quand un fichier PDF est déposé par drag-and-drop */
  onDrop?: (file: File) => void | Promise<void>;
  /** Couleur des icônes de document (défaut : 'grey') */
  iconColor?: 'green' | 'orange' | 'grey';
  /** Icône personnalisée à la place de l'icône PDF par défaut */
  icon?: React.ReactNode;
  /** Masquer le label de type dans la carte (utile quand le titre est déjà affiché au-dessus) */
  hideLabel?: boolean;
  /** Style de carte : 'default' (horizontal) ou 'square' (carré vertical : picto + date) */
  cardStyle?: 'default' | 'square';
  /** Callback "Modifier" affiché dans la modale de preview (remplace le bouton + quand un doc existe) */
  onModify?: () => void;
  /** Style inline appliqué au conteneur racine */
  style?: React.CSSProperties;
  /** Contenu rendu comme dernière "carte" DANS la grille (ex : un champ read-only qui prend la place
   *  d'une/plusieurs cartes). Il flotte avec les cartes (même ligne, même hauteur) sans hack externe. */
  trailingCard?: React.ReactNode;
  /**
   * Nombre de places de carte occupees par `trailingCard`. Defaut 2 (comportement historique) :
   * passer 1 la fait tenir dans une seule place, comme un document. Explicite plutot que porte par un
   * ancetre : ce composant sert 4 vues (BC + MS, acheteur + presta) et un selecteur d'ancetre les
   * ferait diverger en silence.
   */
  trailingCardSpan?: 1 | 2;
}

const DocumentsList: React.FC<DocumentsListProps> = ({
  documents,
  variant = 'inline',
  size = 'default',
  downloadUrlPrefix,
  onAdd,
  onAddLabel,
  onAddDisabled = false,
  addCardError = false,
  onDrop,
  iconColor = 'grey',
  icon,
  hideLabel = false,
  cardStyle = 'default',
  onModify,
  style,
  trailingCard,
  trailingCardSpan = 2,
}) => {
  // Afficher uniquement les documents passés en props (pas de fusion avec documents communs)
  const [allDocs, setAllDocs] = useState<DocumentType[]>([]);
  const [previewDoc, setPreviewDoc] = useState<DocumentType | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [loadingDocKey, setLoadingDocKey] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dropError, setDropError] = useState<string | null>(null);
  const [isDropUploading, setIsDropUploading] = useState(false);

  const canDrop = !!onDrop;

  const executeDrop = async (files: FileList | File[]) => {
    setDropError(null);
    const file = Array.from(files).find(f => f.type === 'application/pdf');
    if (!file) {
      setDropError('Seuls les fichiers PDF sont acceptés.');
      return;
    }
    setIsDropUploading(true);
    try {
      await onDrop!(file);
    } catch (err: any) {
      setDropError(err?.message || 'Erreur lors de l’upload. Veuillez réessayer.');
    } finally {
      setIsDropUploading(false);
    }
  };

  const handleAddCardDragOver = (e: React.DragEvent) => {
    if (!canDrop) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleAddCardDragLeave = (e: React.DragEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleAddCardDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!canDrop) return;
    executeDrop(e.dataTransfer.files);
  };

  // Fonction utilitaire pour générer une clé unique pour un document
  const getDocumentUniqueKey = (doc: DocumentType): string => {
    return doc.key || `${doc.category || 'unknown'}-${doc.id}`;
  };

  // 👇 Fonction pour formater le type
  const formatDocumentType = (type: string): string => {
    if (!type) return '';

    // Si le type contient "CCTP" au début (ou tout seul), on remplace les '-' par des espaces
    if (type.toUpperCase().startsWith('CCTP')) {
      return type.replace(/-/g, ' ').toUpperCase();
    }

    // Sinon on laisse le reste tel quel, juste en majuscules
    return type.toUpperCase();
  };

  // Corrections orthographiques pour les mots sans accent dans les slugs
  const WORD_CORRECTIONS: Record<string, string> = {
    ingenierie: 'Ingénierie',
    batiment: 'Bâtiment',
    electricite: 'Électricité',
  };

  // Formatage pour l'affichage : slug → label lisible
  const formatDisplayType = (type: string): string => {
    if (!type) return '';
    // 1. Lookup dans les labels connus (declaration_honneur → "Déclaration sur l'honneur")
    if (type in DOCUMENT_TYPE_LABELS) {
      return DOCUMENT_TYPE_LABELS[type as keyof typeof DOCUMENT_TYPE_LABELS];
    }
    // 2. CCTP avec tirets : "cctp-travaux" → "CCTP Travaux"
    if (type.includes('-') && type.toUpperCase().startsWith('CCTP')) {
      return type.split('-').map((word) => {
        if (word.toUpperCase() === 'CCTP') return 'CCTP';
        const lower = word.toLowerCase();
        return WORD_CORRECTIONS[lower] ?? (word.charAt(0).toUpperCase() + lower.slice(1));
      }).join(' ');
    }
    // 3. Fallback : retourner tel quel
    return type;
  };

  // 👇 Utiliser directement les documents passés en props (plus de fusion avec contexte)
  useEffect(() => {
    // Dédupliquer par clé unique
    const seenKeys = new Set<string>();
    const deduplicated: DocumentType[] = [];

    for (const doc of documents) {
      const uniqueKey = getDocumentUniqueKey(doc);
      if (!seenKeys.has(uniqueKey)) {
        seenKeys.add(uniqueKey);
        deduplicated.push(doc);
      }
    }

    setAllDocs(deduplicated);
  }, [documents]);

  useEffect(() => {
    if (previewBlobUrl) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [previewBlobUrl]);

  const handleDownload = async (doc: DocumentType) => {
    const docKey = getDocumentUniqueKey(doc);
    setLoadingDocKey(docKey);
    try {
      // Si le document a un binaire (base64), télécharger directement
      if (doc.binaire) {
        const byteCharacters = atob(doc.binaire);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = doc.filename || `${formatDocumentType(doc.type)}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(blobUrl);
        setLoadingDocKey(null);
        return;
      }

      // Sinon, déterminer l'URL en fonction du type de document
      let url: string;

      if (doc.type.startsWith('Contrat')) {
        // Ancien comportement pour les contrats
        url = `${process.env.REACT_APP_API_URL}/contrats/${doc.id}/pdf`;
      } else if (doc.category === 'commun' || doc.type.toLowerCase().startsWith('cctp')) {
        // Documents communs (CCTP, bibliographie, etc.)
        url = `${process.env.REACT_APP_API_URL}/documents-communs/${doc.id}/download`;
      } else if (doc.key) {
        // Documents entreprise ou accord-cadre avec clé UUID
        url = downloadUrlPrefix
          ? `${process.env.REACT_APP_API_URL}/${downloadUrlPrefix}/${doc.key}/download`
          : `${process.env.REACT_APP_API_URL}/documents/key/${doc.key}/download`;
      } else {
        // Fallback pour les anciens documents sans clé
        url = `${process.env.REACT_APP_API_URL}/documents/${doc.id}/download`;
      }

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });

      if (!res.ok) throw new Error('Erreur téléchargement');
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      // Utiliser le filename si disponible, sinon le type formaté
      link.download = doc.filename || `${formatDocumentType(doc.type)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDocKey(null);
    }
  };

  const handlePreview = async (doc: DocumentType) => {
    const docKey = getDocumentUniqueKey(doc);
    setLoadingDocKey(docKey);
    try {
      // Si le document a un binaire (base64), prévisualiser directement
      if (doc.binaire) {
        const byteCharacters = atob(doc.binaire);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        setPreviewBlobUrl(blobUrl);
        setPreviewDoc(doc);
        setLoadingDocKey(null);
        return;
      }

      // Sinon, déterminer l'URL en fonction du type de document
      let url: string;

      if (doc.type.startsWith('Contrat')) {
        // Ancien comportement pour les contrats
        url = `${process.env.REACT_APP_API_URL}/contrats/${doc.id}/pdf`;
      } else if (doc.category === 'commun' || doc.type.toLowerCase().startsWith('cctp')) {
        // Documents communs (CCTP, bibliographie, etc.)
        url = `${process.env.REACT_APP_API_URL}/documents-communs/${doc.id}/download`;
      } else if (doc.key) {
        // Documents entreprise ou accord-cadre avec clé UUID
        url = downloadUrlPrefix
          ? `${process.env.REACT_APP_API_URL}/${downloadUrlPrefix}/${doc.key}/download`
          : `${process.env.REACT_APP_API_URL}/documents/key/${doc.key}/download`;
      } else {
        // Fallback pour les anciens documents sans clé
        url = `${process.env.REACT_APP_API_URL}/documents/${doc.id}/download`;
      }

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });

      if (!res.ok) throw new Error('Erreur prévisualisation');
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPreviewBlobUrl(blobUrl);
      setPreviewDoc(doc);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDocKey(null);
    }
  };

  const closeModal = () => {
    setPreviewBlobUrl(null);
    setPreviewDoc(null);
  };

  return (
    <div
      className="documents-list"
      style={style}
      onDragOver={canDrop ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setIsDragOver(true); } : undefined}
      onDragLeave={canDrop ? (e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragOver(false); } : undefined}
      onDrop={canDrop ? (e) => { e.preventDefault(); setIsDragOver(false); executeDrop(e.dataTransfer.files); } : undefined}
    >
      <div
        className={`documents-grid documents-grid-${variant} ${size === 'smaller' ? 'documents-grid-smaller' : ''}`}
      >
        {allDocs.map((doc) => {
          const docKey = getDocumentUniqueKey(doc);
          const isLoading = loadingDocKey === docKey;
          return (
            <div
              key={docKey}
              className={`document-card document-card-${variant} ${size === 'smaller' ? 'document-card-smaller' : ''} ${cardStyle === 'square' ? 'document-card--square' : ''}`}
              onClick={() => handlePreview(doc)}
              title="Prévisualiser"
            >
              {isLoading ? (
                <div className={`document-card-skeleton ${size === 'smaller' ? 'document-card-skeleton--smaller' : ''} ${cardStyle === 'square' ? 'document-card-skeleton--square' : ''}`}>
                  <div className="doc-skeleton-icon" />
                  <div className="doc-skeleton-body">
                    <div className="doc-skeleton-line doc-skeleton-line--title" />
                    <div className="doc-skeleton-line doc-skeleton-line--date" />
                  </div>
                </div>
              ) : (
                <>
                  {/* Icône PDF ou icône personnalisée */}
                  <div className={`document-card-icon document-card-icon--${iconColor}`}>
                    {icon ?? (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                      </svg>
                    )}
                  </div>

                  {/* Label type + date */}
                  <div className="document-card-body">
                    {!hideLabel && (
                      <span className="document-card-type">{doc.displayLabel || formatDisplayType(doc.type)}</span>
                    )}
                    <span className={`document-card-date ${cardStyle === 'square' ? 'document-card-date--square' : ''}`}>
                      {`Mis à jour le ${formatDate(new Date(doc.updated_at), false)}`}
                    </span>
                  </div>


                </>
              )}
            </div>
          );
        })}

        {/* Carte "+" : uniquement si aucun document (premier ajout) */}
        {onAdd && allDocs.length === 0 && (
          <button
            className={`document-add-card ${size === 'smaller' ? 'document-add-card-smaller' : ''}${isDragOver ? ' document-add-card--drag-over' : ''}${addCardError ? ' document-add-card--error' : ''}`}
            onClick={onAdd}
            disabled={onAddDisabled || isDropUploading}
            title={onAddLabel || 'Ajouter un document'}
          >
            <span className="document-add-card-plus">+</span>
            <span className="document-add-card-label">{onAddLabel || 'Ajouter'}</span>
          </button>
        )}

        {/* Carte finale optionnelle : occupe DEUX places de carte dans la grille (sizing porte par
            DocumentsList via .documents-grid-trailing). */}
        {trailingCard && (
          <div
            className={`documents-grid-trailing${trailingCardSpan === 1 ? ' documents-grid-trailing--single' : ''}`}
          >
            {trailingCard}
          </div>
        )}
      </div>

      {/* Erreur drop */}
      {dropError && (
        <p style={{ margin: '0.4rem 0 0', fontSize: '1.2rem', color: '#dc2626' }}>{dropError}</p>
      )}

      {previewBlobUrl && previewDoc && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content-document" onClick={(e) => e.stopPropagation()}>
            <h3>{previewDoc.displayLabel || previewDoc.type}</h3>
            <iframe
              src={previewBlobUrl}
              title={previewDoc.displayLabel || previewDoc.type}
              width="100%"
              height="500px"
            />
            <div className="modal-actions">
              {onModify && (
                <Button
                  onClick={() => { closeModal(); onModify(); }}
                  value="Modifier"
                  size="small"
                  variant="secondary"
                />
              )}
              <Button
                onClick={() => handleDownload(previewDoc)}
                icon={<DownloadIcon className="dl-button-icon-white" />}
                value="Télécharger"
                size="small"
              />
              <Button
                onClick={closeModal}
                value="Fermer"
                size="small"
                variant="secondary"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsList;